// src/pages/DocumentManagementSystem/utils/dmsReportToPdf.js
//
// The four document reports of the view modal (Approval History, Revision
// Log, Attachments, Associated Documents) as PDFs, built in the browser with
// the same engine as Print PDF (jsPDF + autotable).
//
// Owner (2026-10-01): the server tried to print them with Chromium, which
// does not run on the App Service, and quietly handed out HTML pages. The
// rows now come from GET /dms/{id}/reports/{kind}?format=json and are laid
// out here; the Revision Log carries the legacy "Initial Release" line and
// two-digit revisions like the Revisions tab and the printout.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfSafe, resolveDmsBrand } from "./dmsToPdf";

const M = 40;
const INK = 33;
const MUTED = 110;

// Column widths (pt) per report; the last column takes what is left.
const WIDTHS = {
    approvals: [150, 70, 110],
    revisions: [96, 30, 100, null, 110, 80],
    attachments: [200, null, 100, 60],
    associated: [90, 90, null, 40, 120],
};

/**
 * @param {object} report  the JSON of GET /dms/{id}/reports/{kind}?format=json
 * @param {string} [logoType]  sswhite | shukla | sss (brand colours)
 * @returns {jsPDF}
 */
export function buildDmsReportDoc(report, logoType) {
    const r = report || {};
    const brand = resolveDmsBrand(logoType);
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter", compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // ── Heading ──────────────────────────────────────────────────────
    doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(INK);
    doc.text(pdfSafe(r.title || "Report"), M, M + 4);
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(INK);
    doc.text(pdfSafe(`${r.docId || ""}  Rev ${r.rev || ""}`), M, M + 24);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(MUTED);
    const titleLines = doc.splitTextToSize(pdfSafe(r.docTitle || ""), pageW - M * 2);
    doc.text(titleLines, M, M + 40);
    let y = M + 40 + titleLines.length * 13;
    doc.setFontSize(9);
    doc.text(pdfSafe(`Author: ${r.author || "—"}    Status: ${r.status || "—"}    Printed: ${r.printed || ""}`), M, y);
    y += 8;
    doc.setDrawColor(...brand.colors.primary).setLineWidth(1.2);
    doc.line(M, y, pageW - M, y);
    y += 12;

    // ── Table ────────────────────────────────────────────────────────
    const head = Array.isArray(r.head) ? r.head : [];
    const rows = Array.isArray(r.rows) ? r.rows : [];
    const widths = WIDTHS[r.kind] || [];
    const columnStyles = {};
    widths.forEach((w, i) => { if (w) columnStyles[i] = { cellWidth: w }; });
    autoTable(doc, {
        startY: y,
        margin: { left: M, right: M, top: M, bottom: 48 },
        theme: "grid",
        head: [head.map(pdfSafe)],
        body: rows.length
            ? rows.map((row) => row.map(pdfSafe))
            : [[{ content: "No entries.", colSpan: head.length || 1, styles: { textColor: MUTED, fontStyle: "italic" } }]],
        styles: {
            font: "helvetica", fontSize: 8.5, cellPadding: 4, textColor: INK,
            lineColor: [200, 200, 200], lineWidth: 0.5, valign: "top", overflow: "linebreak",
        },
        headStyles: { fillColor: brand.colors.tableHead, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
        columnStyles,
    });

    // ── Footer: page numbers ─────────────────────────────────────────
    const pageCount = doc.internal.getNumberOfPages();
    for (let page = 1; page <= pageCount; page++) {
        doc.setPage(page);
        doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(MUTED);
        doc.text(pdfSafe(`${r.docId || ""} Rev ${r.rev || ""}  ·  ${r.title || ""}`), M, pageH - 22);
        doc.text(`Page ${page} of ${pageCount}`, pageW - M, pageH - 22, { align: "right" });
    }
    return doc;
}

/** File name of the download: WI-17132-revisions-report.pdf */
export function dmsReportFileName(report) {
    const id = String(report?.docId || "document").replace(/[^A-Za-z0-9._-]/g, "_");
    return `${id}-${report?.kind || "report"}-report.pdf`;
}
